import React from 'react'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { withTranslation } from 'react-i18next'
import { animateScroll as scroll, Element, scroller } from 'react-scroll'
import RemixViewer from '../../common/components/RemixViewer/RemixViewer.jsx'
import { convertTemplateUrlToRemixHTMLUrl } from '../../common/components/RemixViewer/utils.js'
import Style from 'style-it'
import ReactTooltip from 'react-tooltip'

import Loader from '../../components/Loader'
import Sidebar from './Sidebar'
import Modal from '../../components/Modal'
import { API__PROJECTS, API__TEMPLATES, API__USER } from '../../api'
import PremiumTemplatesModal from './PremiumTemplatesModal'

import { uuidv4, getEmbedDynamically } from '../../utils/publisher'
import Toast from '../../common/components/Toast/Toast'
import TemplatesCarousel from '../../components/TemplatesCarousel'

import {
    IconBottomSlide,
    IconArrowDown,
    IconDeviceDesktop,
    IconDeviceMobile,
    IconCross,
    IconComingSoon,
} from '../../svg'

import arrowRight_img from '../../images/png/arrow_right.png'

import { TrialEndedModal } from '../../common/components/Modal/TrialEndedModal/TrialEndedModal'
import { NEW_SUB_TO_TRIAL, OLD_SUB_TO_TRIAL, TRIAL_ENDED_PRODUCT_CODE } from '../../common/constants/ProductConstants'
import { USER__SET_NOTIFICATIONS, USER__SET_SUBSCRIPTION, USER__SET_TRIAL_COUNT } from '../../store/actions'

class TemplateGallery extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            isReady: false,
            isTemplatesLoading: false,

            sidebarData: [],
            availableSectionsIds: [],
            selectedSectionId: null,
            templates: [],
            minimalTemplates: [],

            isMobileSidebarOpened: false,

            previewModal: {
                isOpened: false,
                item: null,
                device: 'desktop',
            },

            trialEndedModal: {
                isOpened: false,
            },

            premiumTemplatesModal: {
                isOpened: false,
            },
        }
    }

    async componentDidMount() {
        try {
            const { match, history, user } = this.props
            const sections = await API__TEMPLATES.GET_SECTIONS()

            if (!sections.length) {
                history.push(`/404`)
                return
            }

            const sectionsData = sections.map(el => {
                return {
                    ...el,
                    customId: el.customId.toUpperCase(),
                }
            })

            const sidebarData = sectionsData.map(el => {
                return {
                    label: el.name,
                    type: 'templates',
                    disabled: false,
                    payload: el,
                }
            })

            const availableSectionsIds = sectionsData.map(el => {
                return el.customId
            })

            let selectedSectionId

            const id = match.params.id
            if (id && availableSectionsIds.includes(id.toUpperCase())) {
                selectedSectionId = id.toUpperCase()
            } else {
                selectedSectionId = availableSectionsIds[0].toUpperCase()
                history.push(`/template-gallery/${availableSectionsIds[0].toLowerCase()}`)
            }

            await this.setState({
                isReady: true,
                sectionsData,
                sidebarData,
                availableSectionsIds,
                selectedSectionId,
            })

            await this.getTemplates(selectedSectionId)
            if (user && user.id) {
                await this.getUserNotifications()
                await this.getUserSubscripiton()
                await this.getUserTrialCount()
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }

    createProject = async template => {
        const { t } = this.props

        const defaultImageUrl = await API__PROJECTS.GET_DEFAULT_PREVIEW_IMAGE()

        const projectId = uuidv4()
        return await API__PROJECTS.SAVE_PROJECT({
            id: null,
            projectId,
            templateId: template.id,
            projectType: {
                id: template.projectType.id,
            },
            projectStructureJson: template.projectStructureJson,
            projectActions: [
                {
                    id: null,
                    actionType: 'LOGO_CLICK',
                    name: 'Click on logo',
                },
            ],
            projectForms: [],
            name: t('_pages._editor.project_default_name'),
            title: t('_pages._editor.project_default_title'),
            description: t('_pages._editor.project_default_description'),
            customImageSelected: false,
            imageUrl: defaultImageUrl,
        })
    }

    getUserNotifications = async () => {
        this.props.dispatch(USER__SET_NOTIFICATIONS(await API__USER.GET_NOTIFICATIONS(this.props.user.id)))
    }

    getUserSubscripiton = async () => {
        this.props.dispatch(USER__SET_SUBSCRIPTION(await API__USER.GET_SELECTED_SUBSCRIPTION(this.props.user.id)))
    }

    getUserTrialCount = async () => {
        this.props.dispatch(USER__SET_TRIAL_COUNT(await API__USER.GET_TRIAL_COUNT(this.props.user.id)))
    }

    onChangeSection = async id => {
        const { history } = this.props

        await this.setState({
            selectedSectionId: id,
        })

        this.closeMobileSidebar()

        await this.getTemplates(id)

        history.push(`/template-gallery/${id.toLowerCase()}`)
    }

    async getTemplates(id, clear = true, preventTopScroll = false) {
        try {
            await this.setState({
                isTemplatesLoading: true,
            })

            if (!preventTopScroll) {
                scroll.scrollToTop({
                    duration: 400,
                })
            }

            const responses = await Promise.all([
                API__TEMPLATES.GET_TEMPLATES(id, {
                    sort: 'orderNumber,asc',
                    page: 0,
                    size: 500,
                    isMinimal: false,
                }),
                API__TEMPLATES.GET_TEMPLATES(id, {
                    sort: 'orderNumber,asc',
                    page: 0,
                    size: 500,
                    isMinimal: true,
                }),
            ])

            await this.setState({
                templates: responses[0].content,
                minimalTemplates: responses[1].content,
            })
        } catch (err) {
            console.error(err)

            if (err.response) {
                const {
                    response: { status },
                } = err

                if (status === 400 || status === 404) {
                    this.props.history.push(`/404`)
                }
            }
        } finally {
            await this.setState({
                isTemplatesLoading: false,
            })
        }
    }

    scrollTo = offset => () => {
        scroller.scrollTo('content_start', {
            offset,
            smooth: true,
            duration: 400,
        })
    }
    openEditor = async template => {
        if (!this.props.user) return this.props.history.push('/auth?action=registration')

        if (template.projectType.projectType === 'NEW_BLOCK_ARTICLE') {
            let project
            try {
                project = await this.createProject(template)
                this.props.history.push(`/editor/${project.projectId}`)
            } catch (err) {
                const message = err.response && err.response.data && err.response.data.message
                if (message && message === 'You already get max premium templates in this subscription period') {
                    this.closePreviewModal()
                    this.openPremiumTemplatesModal()
                } else {
                    console.error(err)
                    Toast('error', {
                        title: 'Error',
                        message: 'Cannot create project from this template',
                    })
                }
            }
        } else {
            this.props.history.push(`/account/editor?templateId=${template.id}`)
        }
    }

    openMobileSidebar = () => {
        this.setState({
            isMobileSidebarOpened: true,
        })
    }
    closeMobileSidebar = () => {
        this.setState({
            isMobileSidebarOpened: false,
        })
    }

    changePreviewModalDevice = device => {
        const { previewModal } = this.state

        this.setState(prevState => ({
            previewModal: {
                ...prevState.previewModal,
                device,
            },
        }))
    }

    openPreviewModal = async template => {
        await this.setState(prevState => ({
            previewModal: {
                ...prevState.previewModal,
                isOpened: true,
                item: template,
                remixUrl: convertTemplateUrlToRemixHTMLUrl(template.publishedProject),
                device: 'desktop',
            },
        }))
    }

    closePreviewModal = () => {
        this.setState(prevState => ({
            previewModal: {
                ...prevState.previewModal,
                isOpened: false,
                item: null,
            },
        }))
    }

    openTrialEndedModal = () => {
        this.setState({
            trialEndedModal: {
                isOpened: true,
            },
        })
    }

    closePreviewAndOpenTrialEndedModal = () => {
        this.closePreviewModal()
        this.openTrialEndedModal()
    }

    closeTrialEndedModal = () => {
        this.setState({
            trialEndedModal: {
                isOpened: false,
            },
        })
    }

    openPremiumTemplatesModal = () => {
        this.setState({
            premiumTemplatesModal: {
                isOpened: true,
            },
        })
    }

    closePremiumTemplatesModal = () => {
        this.setState({
            premiumTemplatesModal: {
                isOpened: false,
            },
        })
    }

    getIsTrial = (userNotifications, userSubscription) => {
        if (userNotifications && userSubscription) {
            return (
                userNotifications.find(obj => [OLD_SUB_TO_TRIAL, NEW_SUB_TO_TRIAL].includes(obj.type)) ||
                userSubscription.product.productCode === TRIAL_ENDED_PRODUCT_CODE
            )
        }
        return false
    }

    getUseLayoutButton = (item, isTrial) => {
        const { t } = this.props
        const buttonProps = item.isPremium
            ? {
                  'data-tip': t('This template contains<br/>premium features'),
                  'data-for': `tooltip-button-${item.id}`,
                  'data-html': true,
              }
            : {}

        return (
            <>
                <button
                    className="button"
                    data-size="md"
                    data-variant="primary"
                    data-test="use-this-layout"
                    onClick={isTrial ? () => this.openTrialEndedModal() : () => this.openEditor(item)}
                    {...buttonProps}
                >
                    {t('Use this layout')}
                </button>
                {item.isPremium && (
                    <ReactTooltip
                        id={`tooltip-button-${item.id}`}
                        className="tooltip"
                        place="top"
                        effect="solid"
                        type="light"
                        arrowColor="transparent"
                    />
                )}
            </>
        )
    }

    render() {
        const {
            t,
            app_loaded,
            window_height,
            header_height,
            user,
            user_trial_count: userTrialCount,
            user_notifications: userNotifications,
            user_subscription: userSubscription,
            history,
        } = this.props
        const {
            isReady,
            sidebarData,
            selectedSectionId,
            isTemplatesLoading,
            templates,
            minimalTemplates,
            isMobileSidebarOpened,
            previewModal,
            trialEndedModal,
            premiumTemplatesModal,
        } = this.state

        const selectedSection =
            selectedSectionId && sidebarData.find(el => el.payload.customId === selectedSectionId).payload

        const isTrial = this.getIsTrial(userNotifications, userSubscription)

        return app_loaded ? (
            isReady && selectedSection ? (
                <div className="page-wrapper">
                    <Helmet>
                        <title>{t('Template gallery')} | Interacty</title>
                    </Helmet>
                    <div className="page page--template-gallery" data-structure="2">
                        <Sidebar
                            height={window_height - header_height}
                            topOffset={header_height}
                            items={sidebarData}
                            activeItemId={selectedSectionId}
                            methods={{
                                onClickItem: id => this.onChangeSection(id),
                            }}
                        />

                        <section className={isTemplatesLoading ? 'dim' : ''}>
                            <div className="sidebar_mobile__btn" onClick={() => this.openMobileSidebar()}>
                                <div className="left">
                                    <div className="icon">
                                        <img src={selectedSection.selectedIconUrl} alt="" />
                                    </div>
                                    <p style={{ color: selectedSection.color }}>{selectedSection.name}</p>
                                </div>
                                <div className="right">
                                    <IconArrowDown />
                                </div>
                            </div>

                            <div className="box" style={{ backgroundColor: selectedSection.color }}>
                                <div className="wrap">
                                    <div className="left">
                                        <div className="title">
                                            <img src={selectedSection.selectedIconUrl} alt="" />
                                            {selectedSection.name}
                                        </div>
                                        <div className="description">{selectedSection.description}</div>
                                    </div>
                                    <div
                                        className="right desktop--image"
                                        style={{ backgroundImage: `url(${selectedSection.imageUrl})` }}
                                    ></div>
                                    <div
                                        className="right mobile--image"
                                        style={{ backgroundImage: `url(${selectedSection.mobileImageUrl})` }}
                                    ></div>
                                </div>
                                <div onClick={this.scrollTo(-header_height)} className="btn">
                                    <IconBottomSlide fill={selectedSection.color} />
                                </div>
                            </div>
                            <Element name="content_start">
                                <h3>{t('Create your quiz from a minimal layout or customize a ready-made project')}</h3>
                            </Element>
                            <div className="content-templates">
                                {minimalTemplates.length ? (
                                    <>
                                        <div className="label">{t('Minimal layouts')}</div>
                                        <TemplatesCarousel
                                            items={minimalTemplates}
                                            isTrial={isTrial}
                                            getUseLayoutButton={this.getUseLayoutButton}
                                        />
                                    </>
                                ) : null}

                                <div className="label">{t('Ready-made projects')}</div>

                                {templates.length ? (
                                    <>
                                        <ul className="templates-list">
                                            {templates.map(item => {
                                                return (
                                                    <li className="templates-list__item" key={item.id}>
                                                        {item.isPremium ? (
                                                            <>
                                                                <div
                                                                    className="premium-mark"
                                                                    data-tip={t('This is a premium<br/>template')}
                                                                    data-for={`tooltip-premium-mark-${item.id}`}
                                                                    data-html={true}
                                                                >
                                                                    <svg
                                                                        width="34"
                                                                        height="34"
                                                                        viewBox="0 0 34 34"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <circle cx="17" cy="17" r="17" fill="#EFF1F7" />
                                                                        <path
                                                                            d="M8.19868 13.6654L10.5001 23.6895C10.5419 23.8712 10.7036 24 10.89 24H23.11C23.2964 24 23.4581 23.8712 23.4999 23.6895L25.8013 13.6654C25.8784 13.3294 25.5229 13.0597 25.2202 13.2246L20.8571 15.6L17.3294 10.4783C17.1704 10.2475 16.8296 10.2475 16.6706 10.4783L13.1429 15.6L8.77981 13.2246C8.47708 13.0597 8.12155 13.3294 8.19868 13.6654Z"
                                                                            stroke="#BFA677"
                                                                            strokeWidth="2"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                                <ReactTooltip
                                                                    id={`tooltip-premium-mark-${item.id}`}
                                                                    className="premium-mark-tooltip"
                                                                    place="bottom"
                                                                    effect="solid"
                                                                    type="light"
                                                                    arrowColor="transparent"
                                                                />
                                                            </>
                                                        ) : null}
                                                        <div className="images">
                                                            <div className="image-container image-container--desktop">
                                                                <div className="layout">
                                                                    <ul>
                                                                        <li></li>
                                                                        <li></li>
                                                                        <li></li>
                                                                    </ul>
                                                                </div>
                                                                <img src={item.projectImage} alt="" />
                                                            </div>
                                                            <div className="image-container image-container--mobile">
                                                                <div className="layout">
                                                                    <span></span>
                                                                </div>
                                                                <img src={item.mobileProjectImage} alt="" />
                                                            </div>
                                                        </div>
                                                        <div className="descr">
                                                            <span
                                                                title={selectedSection.name}
                                                                style={{
                                                                    color: selectedSection.color,
                                                                    borderColor: selectedSection.color,
                                                                    backgroundImage: `url(${selectedSection.selectedIconUrl})`,
                                                                }}
                                                            >
                                                                {selectedSection.name}
                                                            </span>
                                                            <p className="name" title={item.name}>
                                                                {item.name}
                                                            </p>
                                                        </div>
                                                        <div className="btns">
                                                            <button
                                                                className="button"
                                                                data-size="md"
                                                                data-variant="secondary"
                                                                onClick={() => this.openPreviewModal(item)}
                                                            >
                                                                {t('Preview ')}
                                                            </button>
                                                            {this.getUseLayoutButton(item, isTrial)}
                                                        </div>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </>
                                ) : !isTemplatesLoading ? (
                                    t('Nothing was found')
                                ) : null}
                            </div>

                            {isTemplatesLoading ? (
                                <div className="local-loading">
                                    <div className="lds-ellipsis">
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                    </div>
                                </div>
                            ) : null}
                        </section>
                    </div>

                    {premiumTemplatesModal.isOpened ? (
                        <PremiumTemplatesModal onClose={this.closePremiumTemplatesModal} history={history} />
                    ) : null}

                    {trialEndedModal.isOpened ? (
                        <TrialEndedModal
                            t={t}
                            onClose={this.closeTrialEndedModal}
                            userTrialCount={userTrialCount.count}
                            notifications={userNotifications}
                            userId={user.id}
                        />
                    ) : null}

                    {previewModal.isOpened ? (
                        <Modal
                            settings={{ overlay_close: true }}
                            classes={{
                                overlay: 'preview-style',
                                modal: 'preview-style',
                                closeButton: '',
                                closeIcon: '',
                                closeIconCustom: '',
                            }}
                            data={{ open: true }}
                            events={{ onCloseModal: () => this.closePreviewModal() }}
                        >
                            {previewModal.item ? (
                                <>
                                    <div className="top-panel">
                                        <div className="name">{previewModal.item.name}</div>
                                        <ul className="device">
                                            <li
                                                className={`desktop${
                                                    previewModal.device === 'desktop' ? ' is-active' : ''
                                                }`}
                                                onClick={() => this.changePreviewModalDevice('desktop')}
                                            >
                                                <IconDeviceDesktop />
                                            </li>
                                            <li
                                                className={`mobile${
                                                    previewModal.device === 'mobile' ? ' is-active' : ''
                                                }`}
                                                onClick={() => this.changePreviewModalDevice('mobile')}
                                            >
                                                <IconDeviceMobile />
                                            </li>
                                        </ul>
                                        <div className="actions">
                                            <p>{t('Click Edit and create your own project')}</p>
                                            <img src={arrowRight_img} alt="" />
                                            <button
                                                className="button"
                                                data-size="md"
                                                data-variant="primary"
                                                onClick={
                                                    isTrial
                                                        ? () => this.closePreviewAndOpenTrialEndedModal()
                                                        : () => this.openEditor(previewModal.item)
                                                }
                                            >
                                                {t('Edit')}
                                            </button>
                                            <div className="close" onClick={() => this.closePreviewModal()}>
                                                <IconCross />
                                            </div>
                                        </div>
                                    </div>
                                    <RemixViewer remixUrl={previewModal.remixUrl} device={previewModal.device} />
                                </>
                            ) : null}
                        </Modal>
                    ) : null}
                    {isMobileSidebarOpened ? (
                        <Modal
                            settings={{ overlay_close: true }}
                            classes={{
                                overlay: '',
                                modal: 'sidebar-style',
                                closeButton: '',
                                closeIcon: '',
                                closeIconCustom: 'sidebar-style',
                            }}
                            data={{ open: true, max_width: 452 }}
                            events={{ onCloseModal: () => this.closeMobileSidebar() }}
                        >
                            <ul className="sidebar-modal-menu">
                                {sidebarData.map(item => {
                                    return (
                                        <Style key={item.payload.id}>
                                            {`
                                                      .list-item.is-active > .text > p {
                                                         ${
                                                             item.payload.color
                                                                 ? `color: ${item.payload.color} !important;`
                                                                 : ''
                                                         }
                                                      }
                                                      .list-item .icon {
                                                        background-image: url(${item.payload.iconUrl})
                                                      }
                                                      .list-item.is-active .icon {
                                                        background-image: url(${item.payload.selectedIconUrl})
                                                      }
                                                      ${
                                                          item.disabled
                                                              ? `
                                                              .list-item .icon {
                                                                background-image: url(${item.payload.icon})
                                                              }
                                                          `
                                                              : null
                                                      }
                                                    `}
                                            <li
                                                className={`list-item${
                                                    item.payload.customId === selectedSectionId ? ' is-active' : ''
                                                }${item.disabled ? ' is-disabled' : ''}`}
                                                onClick={() => this.onChangeSection(item.payload.customId)}
                                            >
                                                {item.disabled ? (
                                                    <div className="icon"></div>
                                                ) : (
                                                    <div className={item.payload.id ? 'icon' : 'icon--all'}></div>
                                                )}
                                                <div className="text">
                                                    {item.disabled ? (
                                                        <div className="coming-soon">
                                                            <div>
                                                                {item.label}
                                                                <div className="label">{t('coming soon')}</div>
                                                            </div>
                                                            <div>
                                                                <IconComingSoon />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p>{item.label}</p>
                                                    )}
                                                </div>
                                            </li>
                                        </Style>
                                    )
                                })}
                            </ul>
                        </Modal>
                    ) : null}
                </div>
            ) : (
                <Loader text={t('Templates loading...')} />
            )
        ) : null
    }
}

const mapStateToProps = state => ({
    app_loaded: state.app_loaded,
    language: state.language,
    window_height: state._service.styles.window_height,
    header_height: state._service.styles.header_height,
    user_trial_count: state.user_trial_count,
    user_notifications: state.user_notifications,
    user_subscription: state.user_subscription,
    user: state.user,
})

export default compose(connect(mapStateToProps, null), withTranslation('translations'))(TemplateGallery)
